import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    Sunrise, Activity, Users, Star, TrendingUp, AlertTriangle,
    Monitor, MonitorOff, Building2, Clock, ArrowRight, MessageSquare,
    Zap, ChevronRight, RefreshCw, CalendarDays, Stethoscope, Heart, BarChart3
} from 'lucide-react';
import { apiFetch } from '../services/cloudflareService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';

interface Props {
    theme: 'dark' | 'light';
    hospitals: any[];
    patients: any[];
    screens: any[];
    onNavigate?: (tab: string) => void;
}

// Animated number counter
const AnimatedNumber: React.FC<{ value: number; duration?: number; className?: string }> = ({ value, duration = 800, className }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (value === 0) { setDisplay(0); return; }
        let start = 0;
        const step = Math.max(1, Math.ceil(value / (duration / 16)));
        const interval = setInterval(() => {
            start += step;
            if (start >= value) { setDisplay(value); clearInterval(interval); }
            else setDisplay(start);
        }, 16);
        return () => clearInterval(interval);
    }, [value, duration]);
    return <span className={className}>{display.toLocaleString()}</span>;
};

// Mini sparkline component
const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({ data, color, height = 32 }) => {
    if (data.length < 2) return null;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const w = 100;
    const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / range) * (height - 4)}`).join(' ');
    return (
        <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
            <defs>
                <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
            <polygon fill={`url(#spark-${color})`} points={`0,${height} ${points} ${w},${height}`} />
        </svg>
    );
};

const ExecutiveBrief: React.FC<Props> = ({ theme, hospitals, patients, screens, onNavigate }) => {

    const [feedbackData, setFeedbackData] = useState<any>(null);
    const [recentFeedback, setRecentFeedback] = useState<any[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const t = useMemo(() => ({
        bg: theme === 'dark' ? 'bg-[#0B1120]' : 'bg-[#F8FAFC]',
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        cardShadow: theme === 'dark' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]' : 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
    }), [theme]);

    // Derived metrics
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    
    const patientsToday = useMemo(() => patients.filter(p => p.admissionTime && p.admissionTime >= todayStart).length, [patients, todayStart]);
    const patientsActive = useMemo(() => patients.filter(p => p.currentStatus && p.currentStatus !== 'EXIT').length, [patients]);
    const totalHospitals = hospitals.filter(h => h.isActive !== false).length;
    const onlineScreens = screens.filter(s => s.isOnline).length;
    const offlineScreens = screens.filter(s => !s.isOnline).length;
    const totalPatientsProcessed = patients.length;

    // Weekly trend (mock from patients data — last 7 days)
    const weeklyTrend = useMemo(() => {
        const days: number[] = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
            const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
            days.push(patients.filter(p => p.admissionTime && p.admissionTime >= dayStart && p.admissionTime < dayEnd).length);
        }
        return days;
    }, [patients]);

    // Top hospital today
    const topHospitalToday = useMemo(() => {
        const counts: Record<string, number> = {};
        patients.filter(p => p.admissionTime && p.admissionTime >= todayStart).forEach(p => {
            counts[p.hospitalId] = (counts[p.hospitalId] || 0) + 1;
        });
        const topId = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        if (!topId) return null;
        const h = hospitals.find(h => h.id === topId[0]);
        return h ? { name: h.name, count: topId[1], logo: h.branding?.logoUrl } : null;
    }, [patients, hospitals, todayStart]);

    // Alerts
    const alerts = useMemo(() => {
        const list: { type: 'warning' | 'danger' | 'info'; message: string; action?: string }[] = [];
        if (offlineScreens > 0) {
            list.push({ type: 'danger', message: `${offlineScreens} pantalla${offlineScreens > 1 ? 's' : ''} sin señal`, action: 'screens' });
        }
        const inactiveHospitals = hospitals.filter(h => h.isActive === false);
        if (inactiveHospitals.length > 0) {
            list.push({ type: 'warning', message: `${inactiveHospitals.length} hospital${inactiveHospitals.length > 1 ? 'es' : ''} desactivado${inactiveHospitals.length > 1 ? 's' : ''}`, action: 'hospitals' });
        }
        const noStaffHospitals = hospitals.filter(h => h.isActive !== false && (h.totalStaff === 0 || h.totalStaff === undefined));
        if (noStaffHospitals.length > 0) {
            list.push({ type: 'warning', message: `${noStaffHospitals.length} hospital${noStaffHospitals.length > 1 ? 'es' : ''} sin personal registrado`, action: 'hospitals' });
        }
        if (patientsActive > 0) {
            list.push({ type: 'info', message: `${patientsActive} paciente${patientsActive > 1 ? 's' : ''} en proceso ahora mismo` });
        }
        return list;
    }, [hospitals, screens, offlineScreens, patientsActive]);

    // Fetch global feedback stats
    useEffect(() => {
        const fetchFeedback = async () => {
            for (const h of hospitals) {
                try {
                    const res = await apiFetch(`/api/feedback/stats?hospitalId=${h.id}`, { credentials: 'include' });
                    if (res.ok) {
                        const data = await res.json();
                        setFeedbackData(prev => {
                            const merged = { ...(prev || { totalReviews: 0, ratingSum: 0, fiveStarCount: 0, googleClicks: 0 }) };
                            merged.totalReviews += data.totalReviews || 0;
                            merged.ratingSum += (data.averageRating || 0) * (data.totalReviews || 0);
                            merged.fiveStarCount += data.fiveStarCount || 0;
                            merged.googleClicks += data.googleReviewClicks || 0;
                            merged.averageRating = merged.totalReviews > 0 ? Math.round((merged.ratingSum / merged.totalReviews) * 10) / 10 : 0;
                            return merged;
                        });
                    }
                } catch { }
            }
            // Fetch recent feedback items
            for (const h of hospitals.slice(0, 3)) {
                try {
                    const res = await apiFetch(`/api/feedback?hospitalId=${h.id}`, { credentials: 'include' });
                    if (res.ok) {
                        const items = await res.json();
                        if (Array.isArray(items)) {
                            setRecentFeedback(prev => [...prev, ...items.slice(0, 3).map(i => ({ ...i, hospitalName: h.name }))]);
                        }
                    }
                } catch { }
            }
        };
        if (hospitals.length > 0) fetchFeedback();
    }, [hospitals]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        setTimeout(() => setIsRefreshing(false), 1500);
    };

    // Greeting based on time
    const getGreeting = () => {
        const h = now.getHours();
        if (h < 12) return 'Buenos días';
        if (h < 19) return 'Buenas tardes';
        return 'Buenas noches';
    };

    const formattedDate = now.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const cardClass = `${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} p-6 relative overflow-hidden group transition-all duration-300`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-8"
        >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                            <Sunrise className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                            <h2 className={`text-2xl font-black ${t.textWhite} leading-tight tracking-tight`}>{getGreeting()}, Mauricio</h2>
                            <p className={`text-sm ${t.textMuted} font-medium capitalize`}>{formattedDate}</p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleRefresh}
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800'}`}
                >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Actualizar
                </button>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Surgeries Today */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={`${cardClass} cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('history')}
                >
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <CalendarDays className="w-5 h-5" />
                    </div>
                    <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>
                        <AnimatedNumber value={patientsToday} />
                    </p>
                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Cirugías Hoy</p>
                    <div className="mt-3 opacity-60">
                        <Sparkline data={weeklyTrend} color="#10b981" height={28} />
                    </div>
                </motion.div>

                {/* Active Now */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className={`${cardClass} cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('history')}
                >
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <Activity className="w-5 h-5" />
                    </div>
                    <div className="flex items-baseline gap-2">
                        <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>
                            <AnimatedNumber value={patientsActive} />
                        </p>
                        {patientsActive > 0 && (
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                            </span>
                        )}
                    </div>
                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>En Proceso Ahora</p>
                </motion.div>

                {/* Satisfaction */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`${cardClass} cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('marketing')}
                >
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <Star className="w-5 h-5" />
                    </div>
                    <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>
                        {feedbackData?.averageRating ? `${feedbackData.averageRating}` : '—'}
                    </p>
                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Satisfacción</p>
                    {feedbackData?.totalReviews > 0 && (
                        <div className="flex items-center gap-1 mt-2">
                            {[1, 2, 3, 4, 5].map(s => (
                                <Star
                                    key={s}
                                    className={`w-3 h-3 ${s <= Math.round(feedbackData?.averageRating || 0) ? 'text-amber-400 fill-amber-400' : `${t.textMuted} opacity-30`}`}
                                />
                            ))}
                            <span className={`text-[10px] ${t.textMuted} font-medium ml-1`}>({feedbackData?.totalReviews})</span>
                        </div>
                    )}
                </motion.div>

                {/* Hospitals */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className={`${cardClass} cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('hospitals')}
                >
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-600 border border-blue-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <Building2 className="w-5 h-5" />
                    </div>
                    <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>
                        <AnimatedNumber value={totalHospitals} />
                    </p>
                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Hospitales Activos</p>
                </motion.div>

                {/* Total Processed */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`${cardClass} cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('history')}
                >
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' : 'bg-violet-50 text-violet-600 border border-violet-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <TrendingUp className="w-5 h-5" />
                    </div>
                    <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>
                        <AnimatedNumber value={totalPatientsProcessed} />
                    </p>
                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Procesados Total</p>
                </motion.div>
            </div>

            {/* Bottom Row: Alerts + Recent Feedback + Top Hospital */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Alerts Panel */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`${cardClass} lg:col-span-1 cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                >
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                        <AlertTriangle className={`w-4 h-4 ${alerts.some(a => a.type === 'danger') ? 'text-red-400' : 'text-amber-400'}`} />
                        Alertas
                        {alerts.length > 0 && (
                            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${alerts.some(a => a.type === 'danger') ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>
                                {alerts.length}
                            </span>
                        )}
                    </h3>
                    {alerts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                            <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-emerald-500/10' : 'bg-emerald-50'} flex items-center justify-center mb-3`}>
                                <Heart className="w-6 h-6 text-emerald-500" />
                            </div>
                            <p className={`text-sm font-bold ${t.textWhite}`}>Todo en orden</p>
                            <p className={`text-xs ${t.textMuted} mt-1`}>Sin alertas activas</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {alerts.map((alert, i) => (
                                <button
                                    key={i}
                                    onClick={() => alert.action && onNavigate?.(alert.action)}
                                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all ${
                                        alert.type === 'danger'
                                            ? `${theme === 'dark' ? 'bg-red-500/5 border border-red-500/20 hover:bg-red-500/10' : 'bg-red-50 border border-red-200 hover:bg-red-100'}`
                                            : alert.type === 'warning'
                                            ? `${theme === 'dark' ? 'bg-amber-500/5 border border-amber-500/20 hover:bg-amber-500/10' : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'}`
                                            : `${theme === 'dark' ? 'bg-blue-500/5 border border-blue-500/20 hover:bg-blue-500/10' : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'}`
                                    }`}
                                >
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                        alert.type === 'danger' ? 'bg-red-400 animate-pulse' :
                                        alert.type === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
                                    }`} />
                                    <span className={`text-sm font-medium ${t.textWhite} flex-1`}>{alert.message}</span>
                                    {alert.action && <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />}
                                </button>
                            ))}
                        </div>
                    )}
                </motion.div>

                {/* Recent Feedback */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className={`${cardClass} lg:col-span-1 cursor-pointer hover:-translate-y-1 hover:shadow-xl`}
                    onClick={() => onNavigate?.('marketing')}
                >
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                        <MessageSquare className="w-4 h-4 text-indigo-400" />
                        Últimas Opiniones
                        <ChevronRight className={`w-4 h-4 ${t.textMuted} ml-auto opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </h3>
                    {recentFeedback.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                            <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-indigo-500/10' : 'bg-indigo-50'} flex items-center justify-center mb-3`}>
                                <MessageSquare className="w-6 h-6 text-indigo-400" />
                            </div>
                            <p className={`text-sm font-bold ${t.textWhite}`}>Sin opiniones aún</p>
                            <p className={`text-xs ${t.textMuted} mt-1`}>Las calificaciones aparecerán aquí</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentFeedback.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 4).map((fb, i) => (
                                <div key={fb.id || i} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.05]' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100'}`}>
                                    <div className="flex gap-0.5 flex-shrink-0 mt-0.5">
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <Star key={s} className={`w-3 h-3 ${s <= fb.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        {fb.message && <p className={`text-xs ${t.textMain} line-clamp-2`}>{fb.message}</p>}
                                        <p className={`text-[10px] ${t.textMuted} mt-1`}>
                                            {fb.hospitalName} • {new Date(fb.createdAt).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {recentFeedback.length > 0 && (
                        <div className={`mt-4 w-full text-center py-2 text-xs font-bold uppercase tracking-widest rounded-xl ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            Ver todas →
                        </div>
                    )}
                </motion.div>

                {/* Top Hospital + Screen Health */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className={`${cardClass} lg:col-span-1 flex flex-col justify-between`}
                >
                    {/* Top Hospital */}
                    <div
                        className="mb-6 cursor-pointer group/top"
                        onClick={() => onNavigate?.('hospitals')}
                    >
                        <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                            <TrendingUp className="w-4 h-4 text-emerald-400" />
                            Hospital Más Activo Hoy
                            <ChevronRight className={`w-4 h-4 ${t.textMuted} ml-auto opacity-0 group-hover/top:opacity-100 transition-opacity`} />
                        </h3>
                        {topHospitalToday ? (
                            <div className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${theme === 'dark' ? 'bg-emerald-500/5 border border-emerald-500/20 hover:bg-emerald-500/10' : 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'}`}>
                                <div className={`w-12 h-12 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-white'} border ${t.cardBorder} flex items-center justify-center p-2 shadow-sm`}>
                                    {topHospitalToday.logo ? (
                                        <img src={topHospitalToday.logo} className="w-full h-full object-contain" alt="" />
                                    ) : (
                                        <Building2 className="w-6 h-6 text-emerald-500" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-bold ${t.textWhite} truncate`}>{topHospitalToday.name}</p>
                                    <p className={`text-xs text-emerald-500 font-bold`}>{topHospitalToday.count} cirugía{topHospitalToday.count > 1 ? 's' : ''} hoy</p>
                                </div>
                                <Zap className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                            </div>
                        ) : (
                            <div className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${theme === 'dark' ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-slate-50 hover:bg-slate-100'} border ${t.cardBorder}`}>
                                <Stethoscope className={`w-5 h-5 ${t.textMuted}`} />
                                <p className={`text-sm ${t.textMuted} font-medium`}>Sin actividad hoy todavía</p>
                            </div>
                        )}
                    </div>

                    {/* Screen Health */}
                    <div className="cursor-pointer" onClick={() => onNavigate?.('screens')}>
                        <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-3 flex items-center gap-2`}>
                            <Monitor className="w-4 h-4 text-blue-400" />
                            Salud de Pantallas
                            <ChevronRight className={`w-4 h-4 ${t.textMuted} ml-auto opacity-0 group-hover:opacity-100 transition-opacity`} />
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div
                                className={`p-3 rounded-xl text-center transition-all hover:-translate-y-0.5 ${theme === 'dark' ? 'bg-emerald-500/5 border border-emerald-500/20 hover:bg-emerald-500/10' : 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'}`}
                            >
                                <p className={`text-2xl font-black text-emerald-400`}>{onlineScreens}</p>
                                <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-wider`}>Online</p>
                            </div>
                            <div
                                className={`p-3 rounded-xl text-center transition-all hover:-translate-y-0.5 ${offlineScreens > 0 ? (theme === 'dark' ? 'bg-red-500/5 border border-red-500/20 hover:bg-red-500/10' : 'bg-red-50 border border-red-200 hover:bg-red-100') : (theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-slate-50 border border-slate-200')}`}
                            >
                                <p className={`text-2xl font-black ${offlineScreens > 0 ? 'text-red-400' : t.textMuted}`}>{offlineScreens}</p>
                                <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-wider`}>Offline</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Top Hospitals Chart (migrated from Analytics) */}
            {(() => {
                const hospitalVolume: Record<string, { name: string; count: number }> = {};
                patients.forEach(p => {
                    const h = hospitals.find(h => h.id === p.hospitalId);
                    const name = h?.name || p.hospitalId;
                    if (!hospitalVolume[p.hospitalId]) hospitalVolume[p.hospitalId] = { name, count: 0 };
                    hospitalVolume[p.hospitalId].count++;
                });
                const chartData = Object.values(hospitalVolume)
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 5)
                    .map(h => ({ name: h.name.length > 22 ? h.name.substring(0, 22) + '...' : h.name, count: h.count }));
                
                if (chartData.length === 0) return null;
                
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45 }}
                        className={`${cardClass} mt-6`}
                    >
                        <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-6 flex items-center gap-2`}>
                            <BarChart3 className="w-4 h-4 text-indigo-400" /> Top Hospitales por Volumen
                        </h3>
                        <div className="h-[280px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }} layout="vertical" barSize={28}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke={theme === 'dark' ? '#334155' : '#e2e8f0'} />
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: theme === 'dark' ? '#94a3b8' : '#64748b', fontSize: 11, fontWeight: 'bold' }}
                                        width={160}
                                    />
                                    <Tooltip
                                        cursor={{ fill: theme === 'dark' ? '#ffffff05' : '#00000005' }}
                                        contentStyle={{ backgroundColor: theme === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(12px)', border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)', borderRadius: '12px', color: theme === 'dark' ? '#f8fafc' : '#0f172a', fontWeight: 'bold', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                                    />
                                    <Bar dataKey="count" radius={[0, 4, 4, 0]} activeBar={{ fill: theme === 'dark' ? '#818cf8' : '#4f46e5' }}>
                                        {chartData.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#6366f1' : index === 1 ? '#818cf8' : '#a5b4fc'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </motion.div>
                );
            })()}

        </motion.div>
    );
};

export default ExecutiveBrief;
